import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

export interface DraftToast {
  planId: string;
  /** Optional label override — e.g. "Plan saved" instead of the default "Draft saved". */
  label?: string;
}

/** Optional inline recovery affordance rendered as a button on the toast. */
export interface ToastAction {
  label: string;
  run: () => void;
}

export interface ErrorToast {
  message: string;
  /** Optional heading override — defaults to "Couldn't save" in the toast UI. */
  title?: string;
  /** Optional retry/recovery action — renders a button; omit for today's plain toast. */
  action?: ToastAction;
}

/** Generic success toast (title + message), not tied to a plan. */
export interface SuccessToast {
  title:   string;
  message: string;
}

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly _draft$   = new Subject<DraftToast | null>();
  private readonly _error$   = new Subject<ErrorToast | null>();
  private readonly _success$ = new Subject<SuccessToast | null>();

  readonly draft$   = this._draft$.asObservable();
  readonly error$   = this._error$.asObservable();
  readonly success$ = this._success$.asObservable();

  showDraft(planId: string): void {
    this._draft$.next({ planId });
  }

  /** Shows a "Plan saved" success toast — used after editing-mode saves. */
  showSaved(planId: string): void {
    this._draft$.next({ planId, label: 'Plan saved' });
  }

  /** Shows a generic success toast (e.g. after feedback submission). */
  showSuccess(title: string, message: string): void {
    this._success$.next({ title, message });
  }

  /**
   * @param action Optional retry affordance — `{ label, run }` renders a
   * button on the toast. Backward-compatible: existing 1- and 2-arg call
   * sites are unaffected and keep rendering the plain (no-button) toast.
   */
  showError(message: string, title?: string, action?: ToastAction): void {
    this._error$.next({ message, title, action });
  }
}
